// @ts-nocheck
import { cancelReasonLabel, payMethodLabel, estimatedArrival } from "@kawkaw/shared";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Motorbike, Footprints, UtensilsCrossed } from "lucide-react";
import { Button, App, Spin, Divider, Modal } from "../components/admin-ui";
import { OrderCancelModal } from "../components/OrderCancelModal";
import { StatusBadge } from "../components/StatusBadge";
import { useConfirmDialog } from "../components/ConfirmDialog";
import {
  ArrowLeftOutlined, PrinterOutlined, PhoneOutlined,
  EnvironmentOutlined, CarOutlined, ShoppingOutlined,
  ClockCircleOutlined, CheckOutlined, WalletOutlined, TagOutlined,
  CloseCircleOutlined, CheckCircleOutlined, CircleCheck
} from "../components/admin-icons";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import type { Order, OrderItem, Refund, Transaction } from "@kawkaw/shared";
import { AdminShell } from "../layouts/AdminShell";
import {
  useAdminOrder, useAdminCancelOrder, useAdminUpdateOrderStatus,
  useAdminStalls, useAdminTransaction, useLalamoveResubmit,
} from "../api/hooks";
import { downloadReceipt, type ReceiptTxn } from "./ReceiptModal";
import { formatDateTimeDetail, formatOrderDate, formatOrderTime } from "../utils/formatLocaleDate";
import {
  CANCEL_HIDDEN_ORDER_STATUSES,
  ORDER_DETAIL_ACTION_LABEL_KEY,
  TERMINAL_ORDER_STATUSES,
  getOrderCustomerDisplay,
  getOrderStatusAction,
  lalamoveStatusKey,
} from "../utils/orderUi";
import { issueRefundPath, refundActiveNav } from "../utils/refundNavigation";
import { formatTransactionId } from "../utils/transactionUi";
import { useElementHeight } from "../hooks/useElementHeight";

// ── Extended types (Prisma includes extra fields) ────────────────────────────
interface OrderCustomer {
  id: string;
  phone: string;
  name: string | null;
}

interface OrderDetailData extends Order {
  rating?: { stars: number; comment: string | null; created_at: string } | null;
  dispatched_at?: string | null;
  delivered_at?: string | null;
  /** When a stall really started cooking / finished (server timeline), not the POS-accept time. */
  preparing_at?: string | null;
  cancelled_at?: string | null;
  cancel_reason?: string | null;
  cancel_activity?: {
    id: string;
    action: string;
    created_at: string;
    meta?: { prevStatus?: string } | null;
    staff?: { id?: string; name?: string | null; role?: string | null } | null;
  } | null;
  customer_name?: string | null;
  customer_phone?: string | null;
  customer?: OrderCustomer | null;
  items?: (OrderItem & { stall_name?: string })[];
  rider?: {
    id: string;
    name: string;
    platform: string;
    phone: string;
    lalamove_order_id: string | null;
    lalamove_status?: string | null;
    share_link: string | null;
    assigned_at: string;
    plate_number?: string | null;
    /** Time of the last applied Lalamove event (the pickup time while the status is PICKED_UP). */
    lalamove_event_at?: string | null;
    /** Real event times: when a driver was matched, and when the food was picked up. */
    driver_assigned_at?: string | null;
    picked_up_at?: string | null;
  } | null;
  transactions?: Transaction[];
  refunds?: (Refund & { transaction?: Transaction })[];
}

const FOOTER_ACTION_CLASS: Record<string, string> = {
  mark_ready: "!border-none !bg-[#ea580c] !text-white",
  picked_up: "!border-none !bg-[#006c49] !text-white",
  out_for_delivery: "!border-none !bg-[#ea580c] !text-white",
  delivered: "!border-none !bg-[#006c49] !text-white",
};

const DETAIL_PAGE_CLASS = "admin-detail-page min-h-[calc(100vh-64px)] bg-[var(--admin-canvas)] px-[var(--admin-gutter)] max-[1280px]:!px-0";
const DETAIL_TOPBAR_CLASS = "fixed right-0 top-16 z-40 flex items-center justify-between gap-3 border-b border-[#e5e7eb] bg-white px-8 py-3.5 shadow-[0_8px_20px_rgba(25,28,29,0.03)] left-64 max-[900px]:left-[76px] max-[900px]:px-4 min-[901px]:max-[1280px]:left-[220px] min-[901px]:max-[1280px]:px-5";
const DETAIL_GRID_CLASS = "mx-auto grid items-start gap-10 px-8 py-7 grid-cols-[minmax(0,820px)_minmax(280px,1fr)] max-[900px]:grid-cols-1 max-[900px]:px-4 max-[900px]:py-5 min-[901px]:max-[1280px]:grid-cols-[minmax(0,1fr)] min-[901px]:max-[1280px]:px-5";
const DETAIL_CARD_CLASS = "flex w-full flex-col rounded-[32px] border border-transparent p-8 shadow-[0_12px_16px_rgba(25,28,29,0.04)]";
const DETAIL_INFO_GRID_CLASS = "grid items-center gap-12 grid-cols-2 max-[900px]:grid-cols-1 max-[900px]:gap-6";
const DETAIL_TIME_CARD_CLASS = "flex min-h-[116px] items-center gap-4 rounded-2xl bg-[rgba(243,244,245,0.5)] p-5";
const DETAIL_TIME_ICON_CLASS = "flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white shadow-[0_8px_20px_rgba(25,28,29,0.06)]";
const DETAIL_ADDRESS_CARD_CLASS = "flex min-h-[116px] flex-col justify-center gap-2";
const DETAIL_SIDE_PANEL_CLASS = "sticky top-24 flex flex-col gap-5 max-[1280px]:static";
const DETAIL_FOOTER_BAR_CLASS = "fixed bottom-0 z-[100] flex justify-end rounded-t-[32px] bg-[rgba(255,255,255,0.9)] px-6 py-4 shadow-[0_-12px_32px_rgba(25,28,29,0.04)] backdrop-blur-[6px] left-[288px] w-[calc(100vw_-_320px)] max-[900px]:left-[92px] max-[900px]:w-[calc(100vw_-_108px)] max-[900px]:rounded-t-[20px] max-[900px]:p-4 min-[901px]:max-[1280px]:left-[240px] min-[901px]:max-[1280px]:w-[calc(100vw_-_260px)] min-[901px]:max-[1280px]:p-5";
const DETAIL_FOOTER_INNER_CLASS = "flex w-full max-w-[1480px] items-center justify-end gap-3 bg-transparent p-0 max-[900px]:flex-wrap max-[900px]:justify-start";
const DETAIL_FOOTER_BUTTON_CLASS = "!h-12 !rounded-xl !px-6 !text-[13px] !font-extrabold !uppercase !tracking-[0.8px] max-[900px]:!h-11 max-[900px]:!px-4 max-[900px]:!text-[12px]";
const FOOTER_PRIMARY_BUTTON_CLASS = `${DETAIL_FOOTER_BUTTON_CLASS} !px-8 shadow-[0_10px_24px_rgba(249,115,22,0.24)]`;

// Lalamove's own terminal-failure statuses — the delivery itself has been
// abandoned on their side. The platform deliberately does NOT auto-cancel the
// KawKaw order when this happens (needs a human decision: retry dispatch or
// refund), but that means nothing else visibly distinguishes this from an
// order that's still normally waiting for a rider unless the UI checks for it
// explicitly — found by comparing a real order against Lalamove's own Partner
// Portal, where it showed "Cancelled" while this screen still said "finding a
// rider" with a live dispatch button.
const LALAMOVE_FAILED_STATUSES = new Set(["CANCELED", "REJECTED", "EXPIRED"]);

function StatusPill({ status }: { status: string }) {
  return <StatusBadge status={status} kind="order" size="md" />;
}

function TypePill({ type }: { type: string }) {
  return <StatusBadge status={type === "delivery" ? "delivery" : "pickup"} kind="orderType" size="md" />;
}

// ── Contextual status message ─────────────────────────────────────────────────
function statusMessage(od: OrderDetailData, t: TFunction, lang = "zh"): string {
  // A rider-level Lalamove failure can happen while Order.status itself is
  // still "out_for_delivery"/"in_transit" (there's no separate "failed" order
  // status) — showing the generic in-progress message here would contradict
  // the rider card below, which correctly shows the failure + resubmit button
  // (#244).
  const deliveryDone = od.status === "delivered" || od.status === "completed";
  if (od.rider && (!deliveryDone && od.status !== "cancelled" && LALAMOVE_FAILED_STATUSES.has(od.rider.lalamove_status ?? ""))) {
    return t("orderDetail.statusMsg.deliveryIssue");
  }
  switch (od.status) {
    case "new":              return t("orderDetail.statusMsg.new");
    case "preparing":        return t("orderDetail.statusMsg.preparing");
    case "ready":            return od.type === "pickup" ? t("orderDetail.statusMsg.readyPickup")
      : od.rider?.lalamove_status === "ON_GOING" ? t("orderDetail.statusMsg.outForDelivery")   // rider accepted, still heading to the shop (the order flips to out_for_delivery at pickup)
      : t("orderDetail.statusMsg.readyDelivery");
    case "out_for_delivery": return od.rider?.lalamove_status === "PICKED_UP" ? t("orderDetail.statusMsg.pickedUp")
      : od.rider?.lalamove_status === "ASSIGNING_DRIVER" ? t("orderDetail.statusMsg.readyDelivery")   // a driver rejected and Lalamove is matching a new one
      : t("orderDetail.statusMsg.outForDelivery");
    case "in_transit":       return t("orderDetail.statusMsg.inTransit");
    case "delivered":        return t("orderDetail.statusMsg.delivered");
    case "completed":        return od.type === "pickup" ? t("orderDetail.statusMsg.completedPickup") : t("orderDetail.statusMsg.completedDelivery");
    case "cancelled":        return t("orderDetail.statusMsg.cancelled") + `: ${cancelReasonLabel(od.cancel_reason || "others", lang)}`;
    default:                 return "";
  }
}

// ── Journey step builder ──────────────────────────────────────────────────────
const STATUS_IDX: Record<string, number> = {
  new: 0, preparing: 1, ready: 2, out_for_delivery: 3, in_transit: 4,
  delivered: 5, completed: 6, cancelled: 99,
};

interface JStep {
  label: string;
  time?: string | null;
  state: "done" | "active" | "pending";
  subtext?: string;
  tone?: "default" | "cancelled";
  showDateTime?: boolean;
}

function buildJourney(od: OrderDetailData, t: TFunction): JStep[] {
  if (od.status === "cancelled") {
    const actor = od.cancel_activity?.staff?.name?.trim() || t("orderDetail.journey.unknownCanceller");
    return [
      { label: t("orderDetail.journey.orderCreated"), time: od.created_at, state: "done" },
      ...(od.accepted_at ? [{ label: t("orderDetail.journey.accepted"), time: od.accepted_at, state: "done" as const }] : []),
      {
        label: t("orderDetail.journey.cancelledBy", { name: actor }),
        time: od.cancelled_at ?? od.cancel_activity?.created_at ?? null,
        state: "done",
        tone: "cancelled",
        showDateTime: true,
      },
    ];
  }

  const journeyStatus = od.status;
  const curIdx = STATUS_IDX[journeyStatus] ?? 0;
  const st = (thresholdIdx: number): "done" | "active" | "pending" =>
    curIdx > thresholdIdx ? "done" : curIdx === thresholdIdx ? "active" : "pending";
  const withCancellation = (steps: JStep[]): JStep[] => {
    if (od.status !== "cancelled") return steps;
    const actor = od.cancel_activity?.staff?.name?.trim() || t("orderDetail.journey.unknownCanceller");
    return [
      ...steps,
      {
        label: t("orderDetail.journey.cancelledBy", { name: actor }),
        time: od.cancelled_at ?? od.cancel_activity?.created_at ?? null,
        state: "done",
        tone: "cancelled",
        showDateTime: true,
      },
    ];
  };

  const readyLabel = t("orderDetail.journey.ready");
  const base: JStep[] = [
    { label: t("orderDetail.journey.orderCreated"), time: od.created_at,  state: "done" },
    { label: t("orderDetail.journey.accepted"),      time: od.accepted_at, state: curIdx > 0 ? "done" : "pending" },
    { label: t("orderDetail.journey.preparing"),     time: od.preparing_at ?? null, state: st(1) },
    { label: readyLabel,         time: od.ready_at,    state: st(2) },
  ];

  if (od.type === "delivery") {
    const deliveredTime = od.delivered_at ?? od.completed_at ?? null;
    // A rider row exists as soon as dispatch is booked — only a named driver (not re-matching) is "assigned".
    const rs = od.rider?.lalamove_status ?? "";
    const realDriver = !!od.rider?.name && rs !== "ASSIGNING_DRIVER" && !LALAMOVE_FAILED_STATUSES.has(rs);
    const riderPickedUp = rs === "PICKED_UP" || rs === "COMPLETED";
    const tracked = !!od.rider?.lalamove_order_id;

    // One "current" marker, always on the furthest milestone really reached (a rider heading to the shop moves it past "Ready"):
    // every earlier step is done, every later one pending. Final = delivered → all done.
    const delivered = curIdx >= STATUS_IDX.delivered;
    const pickedTime = riderPickedUp ? od.rider?.picked_up_at ?? (rs === "PICKED_UP" ? od.rider?.lalamove_event_at ?? null : null) : null;
    const outTime = tracked ? (riderPickedUp ? (od.rider?.picked_up_at ?? (rs === "PICKED_UP" ? od.rider?.lalamove_event_at ?? od.dispatched_at ?? null : od.dispatched_at ?? null)) : null) : od.dispatched_at ?? null;
    const pickedUp = tracked ? riderPickedUp || delivered : curIdx >= 4;   // untracked (own courier): "out for delivery" = picked up
    const seq: Array<{ label: string; time?: string | null; reached: boolean; subtext?: string }> = [
      { label: t("orderDetail.journey.orderCreated"), time: od.created_at, reached: true },
      { label: t("orderDetail.journey.accepted"), time: od.accepted_at, reached: curIdx > 0 || !!od.accepted_at },
      { label: t("orderDetail.journey.preparing"), time: od.preparing_at ?? null, reached: curIdx >= 1 },
      { label: readyLabel, time: od.ready_at, reached: curIdx >= 2, subtext: t("orderDetail.journey.waitingPickup") },
      // no real driver (re-matching / booking failed) → still waiting for a rider, not "assigned"
      { label: t("orderDetail.journey.riderAssigned"), time: realDriver ? od.rider?.driver_assigned_at ?? od.rider?.assigned_at ?? null : null, reached: realDriver || pickedUp },
      { label: t("orderDetail.journey.riderArrived"), time: pickedTime, reached: pickedUp },
      { label: t("orderDetail.journey.outForDelivery"), time: outTime, reached: pickedUp },
      { label: t("orderDetail.journey.delivered"), time: deliveredTime, reached: delivered },
    ];
    const last = seq.reduce((a, x, k) => (x.reached ? k : a), 0);
    return withCancellation(seq.map((x, k): JStep => ({
      label: x.label, time: x.time,
      state: delivered || k < last ? "done" : k === last ? "active" : "pending",
      ...(x.subtext && k === last ? { subtext: x.subtext } : {}),
    })));
  }

  const pickedUpState: JStep["state"] = curIdx >= STATUS_IDX.completed ? "done" : "pending";

  return withCancellation([
    ...base,
    { label: t("orderDetail.journey.pickedUp"), time: od.completed_at, state: pickedUpState },
  ]);
}

function paymentMethodLabel(t: TFunction, method: string): string {
  const key = `orderDetail.payment.${method}`;
  const tr = t(key);
  return tr === key ? method : tr;
}

function getDisplaySpecialNote(order: OrderDetailData): string | null {
  const note = order.special_note?.trim();
  if (!note) return null;
  const [, walkInNote] = note.split("—", 2);
  if (note.startsWith("Walk-in") && walkInNote !== undefined) return walkInNote.trim() || null;
  if (note === "Walk-in" || /^Walk-in:\s*/i.test(note)) return null;
  return note;
}

function parseDeliveryAddress(address: string | null | undefined) {
  const raw = address?.trim();
  if (!raw) return { line1: "", note: "" };
  const [geo = "", unit = "", note = ""] = raw.split("||").map((s) => s.trim());
  return {
    line1: unit ? `${unit}, ${geo}` : (geo || raw),
    note,
  };
}

function parseDeliveryContact(note: string | null | undefined) {
  const contactPart = note?.split("—")[0]?.trim() ?? "";
  if (!contactPart.includes("|")) return { name: "", phone: "" };
  const [name = "", phone = ""] = contactPart.split("|").map((s) => s.trim());
  return { name, phone };
}

function formatDeliveryPhone(phone: string | null | undefined) {
  const raw = phone?.trim();
  if (!raw) return "";
  return raw.startsWith("+") ? raw : `+60${raw.replace(/^0/, "")}`;
}

// ── Main screen ───────────────────────────────────────────────────────────────
export function OrderDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { message } = App.useApp();
  const { confirm: confirmDialog } = useConfirmDialog();

  const [cancelOpen, setCancelOpen] = useState(false);
  const [receiptTxnId, setReceiptTxnId] = useState<string | undefined>(undefined);
  const [refundNoPaymentOpen, setRefundNoPaymentOpen] = useState(false);
  const [topbarRef, topbarHeight] = useElementHeight<HTMLDivElement>();

  const { data: orderRes, isLoading } = useAdminOrder(id ?? "");
  const { data: stallsRes } = useAdminStalls();
  const { data: receiptTxnData, isFetching: isReceiptFetching } = useAdminTransaction(receiptTxnId);
  const cancelOrder = useAdminCancelOrder();
  const updateStatus = useAdminUpdateOrderStatus();
  const lalamoveResubmit = useLalamoveResubmit();

  useEffect(() => {
    if (!receiptTxnId || !receiptTxnData?.order?.kopitiam || !receiptTxnData?.order?.items) return;
    downloadReceipt(receiptTxnData as unknown as ReceiptTxn);
    setReceiptTxnId(undefined);
  }, [receiptTxnId, receiptTxnData]);

  const stallMap = new Map<string, string>(
    (stallsRes?.data ?? []).map((s: { id: string; name: string }) => [s.id, s.name])
  );

  // ── Loading / error ─────────────────────────────────────────────────────────
  if (!id) {
    return <AdminShell><div className="p-10">{t("orderDetail.invalidId")}</div></AdminShell>;
  }

  if (isLoading || !orderRes) {
    return (
      <AdminShell>
        <div className="flex min-h-[400px] items-center justify-center">
          <Spin size="large" />
        </div>
      </AdminShell>
    );
  }

  const od = orderRes.data as unknown as OrderDetailData;
  if (!od) {
    return <AdminShell><div className="p-10">{t("orderDetail.notFound")}</div></AdminShell>;
  }

  // ── Derived data ─────────────────────────────────────────────────────────────
  const isTerminal = TERMINAL_ORDER_STATUSES.has(od.status);
  // Handed over: nothing about the rider can still be "waiting" or "failed" on this order.
  const deliveryDone = od.status === "delivered" || od.status === "completed";
  const canCancelOrder = !CANCEL_HIDDEN_ORDER_STATUSES.has(od.status);
  const footerStatusAction = getOrderStatusAction(od);

  const txns = od.transactions ?? [];
  const paidTxn = txns.find((transaction) => transaction.status === "success") ?? null;
  const txn = paidTxn ?? txns[0] ?? null;
  const refunds = od.refunds ?? [];

  // Group items by stall
  const stallGroups = new Map<string, (OrderItem & { stall_name?: string })[]>();
  for (const item of od.items ?? []) {
    const stallKey = item.stall_id;
    if (!stallGroups.has(stallKey)) stallGroups.set(stallKey, []);
    stallGroups.get(stallKey)!.push(item);
  }

  const totalItems = (od.items ?? []).reduce((sum, it) => sum + it.qty, 0);
  // Full breakdown across all items — base price, base markup, add-on cost, add-on
  // markup, each recorded and shown independently rather than blended together.
  // Lines a stall rejected are no longer charged: keep them out of every total below so the rows add up to the re-priced subtotal.
  const isRejectedLine = (it: { note?: string | null }) => !!it.note?.startsWith("[REJECTED");
  let rejectedMarkup = 0;
  const orderTotals = (od.items ?? []).reduce((acc, it) => {
    if (isRejectedLine(it)) {
      rejectedMarkup += (Number((it as { markup_amount?: string }).markup_amount ?? 0) + Number((it as { options_markup_amount?: string }).options_markup_amount ?? 0)) * it.qty;
      return acc;
    }
    const opts = (it.options as Array<{ extra_charge: string }>) ?? [];
    const optsExtraTotal = opts.reduce((s, o) => s + Number(o.extra_charge ?? "0"), 0);
    const optsMarkupTotal = Number((it as { options_markup_amount?: string }).options_markup_amount ?? 0);
    acc.basePriceTotal += Number((it as { base_price?: string }).base_price ?? it.price) * it.qty;
    acc.markupTotal += Number((it as { markup_amount?: string }).markup_amount ?? 0) * it.qty;
    acc.addonCostTotal += optsExtraTotal * it.qty;
    acc.addonMarkupTotal += optsMarkupTotal * it.qty;
    acc.sstTotal += (Number((it as { sst_amount?: string }).sst_amount ?? 0) + Number((it as { options_sst_amount?: string }).options_sst_amount ?? 0)) * it.qty;
    return acc;
  }, { basePriceTotal: 0, markupTotal: 0, addonCostTotal: 0, addonMarkupTotal: 0, sstTotal: 0 });
  const sstIncluded = !!(od as { sst_included?: boolean }).sst_included;   // prices already contain SST (orders from 2026-10-23)
  // The order's own stored markup record (items + bundles + add-ons, snapshotted at order time) is authoritative; bundled
  // units carry no item markup of their own, so summing lines would overstate it. Add-ons are shown on their own row.
  const storedMarkup = (od as { markup_total?: string }).markup_total;
  if (storedMarkup != null) orderTotals.markupTotal = Math.max(0, Number(storedMarkup) - rejectedMarkup - orderTotals.addonMarkupTotal);
  const customerPaidAmount = txns
    .filter((transaction) => transaction.status === "success")
    .reduce((sum, transaction) => sum + Number(transaction.amount ?? 0), 0);
  const journey = buildJourney(od, t);
  const isReadyStepChecked = od.status !== "cancelled" && (Boolean(od.ready_at) || (STATUS_IDX[od.status] ?? 0) >= STATUS_IDX.ready);
  const isPickupCompleted = od.type === "pickup" && od.status === "completed";
  const pickupDisplayTime = isPickupCompleted
    ? (od.completed_at ?? od.ready_at ?? od.est_ready_at ?? null)
    : isReadyStepChecked
    ? (od.ready_at ?? od.est_ready_at ?? null)
    : (od.est_ready_at ?? null);
  const pickupTimeText = pickupDisplayTime
    ? formatOrderTime(pickupDisplayTime, i18n.language)
    : "—";
  const cancelledTimeText = od.cancelled_at ?? od.cancel_activity?.created_at
    ? formatDateTimeDetail(od.cancelled_at ?? od.cancel_activity!.created_at, i18n.language)
    : "—";
  const showAsapBadge = od.status === "ready";
  // What the rider is doing right now — never "waiting for pickup" once the food is in hand.
  const riderStatus = od.rider?.lalamove_status ?? "";
  const riderPhaseNote = riderStatus === "PICKED_UP" || riderStatus === "COMPLETED"
    ? t("orderDetail.statusMsg.pickedUp")
    : riderStatus === "ON_GOING" ? t("orderDetail.riderHeadingToShop") : t("orderDetail.riderWillArrive");

  const guestLabel = t("refundDetail.guest");
  const displayCustomer = getOrderCustomerDisplay(od);
  const customerName = displayCustomer.name ?? displayCustomer.phone ?? guestLabel;
  const customerPhone = displayCustomer.phone;
  const displaySpecialNote = getDisplaySpecialNote(od);
  const deliveryAddress = parseDeliveryAddress(od.address);
  const deliveryContact = parseDeliveryContact(od.special_note);
  const deliveryContactName = displayCustomer.name ?? deliveryContact.name;
  const deliveryContactPhone = formatDeliveryPhone(displayCustomer.phone ?? deliveryContact.phone);
  const initials = customerName === guestLabel ? "G" : customerName.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase();

  // ── Actions ────────────────────────────────────────────────────────────────
  async function handleUpdateStatus(newStatus: string) {
    let note: string | undefined;
    if (newStatus === "delivered") {
      // "Delivered" is a promise to the customer and cannot be undone (the order can no longer be cancelled): always ask, and
      // when the Lalamove rider has not even picked the food up yet, insist on a written reason.
      const riderTracked = !!od.rider?.lalamove_order_id;
      const pickedUp = ["PICKED_UP", "COMPLETED"].includes(od.rider?.lalamove_status ?? "");
      if (riderTracked && !pickedUp) {
        const reason = window.prompt(t("orderDetail.deliverForcePrompt"));
        if (!reason || reason.trim().length < 3) return;
        note = reason.trim();
      } else if (!(await confirmDialog({ title: t("orderDetail.deliverConfirmTitle"), description: t("orderDetail.deliverConfirmDesc"), danger: true }))) {
        return;
      }
    }
    updateStatus.mutate(
      { id: od.id, status: newStatus, ...(note ? { note } : {}) },
      {
        onSuccess: () => message.success(t("orderDetail.statusUpdated")),
        onError: () => message.error(t("orderDetail.updateStatusFailed")),
      }
    );
  }

  async function handleCancel(values: { reason: string }) {
    try {
      await cancelOrder.mutateAsync({ id: od.id, reason: values.reason });
      message.success(t("orderDetail.cancelSuccess"));
      setCancelOpen(false);
    } catch {
      message.error(t("orderDetail.cancelFailed"));
    }
  }

  async function handleResubmitLalamove() {
    const ok = await confirmDialog({
      title: t("orderDetail.resubmitLalamoveTitle"),
      description: t("orderDetail.resubmitLalamoveDesc"),
      confirmLabel: t("orderDetail.resubmitLalamoveConfirm"),
      cancelLabel: t("common.cancel", { defaultValue: "Cancel" }),
    });
    if (!ok) return;
    try {
      await lalamoveResubmit.mutateAsync(od.id);
      message.success(t("orderDetail.resubmitLalamoveSuccess"));
    } catch {
      message.error(t("orderDetail.resubmitLalamoveFailed"));
    }
  }

  function handleDownloadReceipt() {
    if (!txn?.id) {
      message.warning(t("orderDetail.noTransaction"));
      return;
    }
    setReceiptTxnId(txn.id);
  }

  function openRefundForm() {
    if (!paidTxn) {
      setRefundNoPaymentOpen(true);
      return;
    }
    navigate(issueRefundPath(paidTxn.id, "orders"), { state: { activeNav: refundActiveNav("orders") } });
  }

  // Contextual footer action button
  function FooterAction() {
    // A failed Lalamove booking on an order that is not handed over yet: the next step is re-booking, never a dead end.
    if (!footerStatusAction && od.type === "delivery" && !deliveryDone && od.status !== "cancelled" && LALAMOVE_FAILED_STATUSES.has(od.rider?.lalamove_status ?? "")) {
      return (
        <Button type="primary" loading={lalamoveResubmit.isPending} onClick={handleResubmitLalamove} className={`${FOOTER_PRIMARY_BUTTON_CLASS} !border-none !bg-[#ea580c] !text-white`}>
          {t("orderDetail.resubmitLalamove")}
        </Button>
      );
    }
    if (!footerStatusAction) return null;
    return (
      <Button
        icon={<CheckCircleOutlined />}
        className={`${FOOTER_PRIMARY_BUTTON_CLASS} ${FOOTER_ACTION_CLASS[footerStatusAction.actionKey]}`}
        onClick={() => handleUpdateStatus(footerStatusAction.nextStatus)}
      >
        {t(ORDER_DETAIL_ACTION_LABEL_KEY[footerStatusAction.actionKey])}
      </Button>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <AdminShell>
      {/* ── Content wrapper with bottom padding for sticky footer ── */}
      <div className={DETAIL_PAGE_CLASS} style={{ paddingTop: topbarHeight ? topbarHeight + 24 : 57, paddingBottom: 220 }}>

        {/* ── Top bar ── */}
        <div ref={topbarRef} className={DETAIL_TOPBAR_CLASS}>
          <div className="flex min-w-0 items-center gap-3">
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(-1)}
              className="!h-10 !w-10 !rounded-xl !border-none !bg-transparent !p-0 !text-[#534434]"
            />
            <div className="min-w-0">
              <div className="truncate text-xs font-bold uppercase tracking-[0.8px] text-[#867461]">{t("orderDetail.back")}</div>
              <div className="truncate text-base font-black text-[#191c1d]">{od.order_code}</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[11px] font-black uppercase tracking-[0.8px] text-[#867461]">{t("orderDetail.total")}</div>
            <div className="text-xl font-black text-[#ea580c]">RM {Number(od.total).toFixed(2)}</div>
          </div>
        </div>

        {/* ── 2-column grid ── */}
        <div className={DETAIL_GRID_CLASS}>

          {/* ══ LEFT COLUMN ══ */}
          <div id="receipt-print-area" className="flex flex-col gap-5">

            {/* ── Order Header + Customer (single merged card matching Figma 42-6234) ── */}
            <div className={`${DETAIL_CARD_CLASS} bg-white`}>

              {/* Row 1: pills (left) + STATUS MESSAGE (right) */}
              <div className="flex justify-between">
                <div className="flex flex-col gap-4">
                  <div className="flex gap-2">
                    <StatusPill status={od.status} />
                    <TypePill type={od.type} />
                  </div>

                  {/* order title */}
                  <div className="text-xl sm:text-3xl font-extrabold leading-tight text-[#191c1d]">
                    {t("orderDetail.orderPrefix")} {od.order_code}
                  </div>

                  {/* Row 3: time + paid */}
                  <div className="admin-order-meta flex flex-wrap items-center gap-3">
                    <span className="text-[#534434] text-sm flex items-center gap-1 uppercase">
                      <ClockCircleOutlined />
                      {formatOrderDate(od.created_at, i18n.language)} {formatOrderTime(od.created_at, i18n.language)}
                    </span>
                    {paidTxn && (
                      <span className="shrink-0 whitespace-nowrap text-[#15803D] text-sm flex items-center gap-1 uppercase">
                        <CircleCheck className="text-sm" />
                        {t("orderDetail.paid")}
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-end flex flex-col gap-1 max-w-[260px]">
                  <div className="text-[#534434] text-xs uppercase font-bold">{t("orderDetail.statusMessageLabel")}</div>
                  <div className="text-base font-semibold text-[#ea580c]">{statusMessage(od, t, i18n.language)}</div>
                </div>
              </div>

              {/* Divider */}
              <Divider className="m-0" />

              {/* Row 4: Customer (left) + Pickup/Delivery info (right) */}
              <div className={DETAIL_INFO_GRID_CLASS}>

                {/* Customer */}
                <div className="flex items-center gap-4">
                  <div className="flex size-12 shrink-0 items-center justify-center rounded-2xl bg-[#f3f4f5] text-base font-bold text-[#191c1d]">
                    {initials}
                  </div>
                  <div className="flex flex-col gap-2">
                    <div className="text-[#534434] text-xs font-bold uppercase">{t("orderDetail.customer")}</div>
                    <div className="text-xl font-bold uppercase leading-none text-[#191c1d]">{customerName}</div>
                    {customerPhone && (
                      <a href={`tel:${customerPhone}`} className="text-[#006C49] text-sm flex items-center gap-1">
                        <PhoneOutlined />
                        {customerPhone}
                      </a>
                    )}
                  </div>
                </div>

                {/* Pickup time / Delivery address — inside a grey rounded box */}
                {od.type === "pickup" ? (
                  <div className={DETAIL_TIME_CARD_CLASS}>
                    <div className={`${DETAIL_TIME_ICON_CLASS} ${od.status === "cancelled" ? "text-[#dc2626]" : "text-[#ea580c]"}`}>
                      {od.status === "cancelled" ? (
                        <CloseCircleOutlined className="text-[24px]" />
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" width="19" height="25" viewBox="0 0 19 25" fill="none">
                          <path d="M0 25V17.5H3.75V25H0ZM6.25 25V10.125C5.20833 10.4792 4.53125 11.1302 4.21875 12.0781C3.90625 13.026 3.75 14 3.75 15H1.25C1.25 12.3333 2.03125 10.2083 3.59375 8.625C5.15625 7.04167 7.29167 6.25 10 6.25C12.0833 6.25 13.6458 5.73438 14.6875 4.70312C15.7292 3.67188 16.25 2.10417 16.25 0H18.75C18.75 1.83333 18.3594 3.47396 17.5781 4.92188C16.7969 6.36979 15.5208 7.39583 13.75 8V25H11.25V17.5H8.75V25H6.25ZM10 5C9.3125 5 8.72396 4.75521 8.23438 4.26562C7.74479 3.77604 7.5 3.1875 7.5 2.5C7.5 1.8125 7.74479 1.22396 8.23438 0.734375C8.72396 0.244792 9.3125 0 10 0C10.6875 0 11.276 0.244792 11.7656 0.734375C12.2552 1.22396 12.5 1.8125 12.5 2.5C12.5 3.1875 12.2552 3.77604 11.7656 4.26562C11.276 4.75521 10.6875 5 10 5Z" fill="#F59E0B"/>
                        </svg>
                      )}
                    </div>
                    <div>
                      <div className="mb-1 text-xs font-bold uppercase tracking-[1.2px] text-[#867461]">
                        {od.status === "cancelled" ? t("orderDetail.cancelledTime") : t("orderDetail.pickupTime")}
                      </div>
                      <div className="mb-1 flex items-center gap-2">
                        <span className={`text-[22px] font-extrabold leading-none ${od.status === "cancelled" ? "text-[#dc2626]" : "text-[#ea580c]"}`}>
                          {od.status === "cancelled" ? cancelledTimeText : pickupTimeText}
                        </span>
                        {od.status !== "cancelled" && showAsapBadge && (
                          <span className="rounded-full bg-[#fff0e0] px-2.5 py-0.5 text-[11px] font-bold text-[#ea580c]">{t("orderDetail.asap")}</span>
                        )}
                      </div>
                      <div className="text-xs text-[#867461]">
                        {od.status === "cancelled" ? t("orderDetail.orderCancelledAt") : t("orderDetail.customerWillArrive")}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className={DETAIL_ADDRESS_CARD_CLASS}>
                    <div className="text-xs font-bold text-[#867461] uppercase">{t("orderDetail.deliveryAddress")}</div>
                    <div className="flex flex-col gap-2 text-sm text-[#191c1d]">
                      <div className="uppercase">{deliveryAddress.line1 || "—"}</div>
                      {deliveryAddress.note ? (
                        <div className="text-xs italic uppercase text-[#191c1d]">
                          {deliveryAddress.note}
                        </div>
                      ) : null}
                    </div>
                    {Number(od.distance_km) > 0 && (
                      <div className="flex items-center gap-1 text-[13px] font-bold text-[#ea580c]">
                        <EnvironmentOutlined className="text-[13px]" />
                        {Number(od.distance_km).toFixed(1)} km
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Rider section (delivery only) */}
              {od.type === "delivery" && od.rider && (
                <>
                  <Divider className="m-0" />
                  <div className={DETAIL_INFO_GRID_CLASS}>
                    {/* Left: rider info — dispatch has succeeded (a Rider row
                        exists) but no real driver may be assigned by Lalamove
                        yet, so never render a blank/placeholder name as if it
                        were a real driver's identity. Also excludes
                        ASSIGNING_DRIVER: a prior driver can reject and Lalamove
                        regresses to re-matching, but `name` is deliberately not
                        cleared on that non-terminal case (only on real
                        failure) — staff must not see a stale driver as if
                        still assigned. */}
                    <div className="flex items-center gap-4">
                      {od.rider.name && od.rider.lalamove_status !== "ASSIGNING_DRIVER" ? (
                        <div className="flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-[#8a3b0a] text-base font-bold text-white">
                          {od.rider.name.slice(0, 2).toUpperCase()}
                        </div>
                      ) : (!deliveryDone && od.status !== "cancelled" && LALAMOVE_FAILED_STATUSES.has(od.rider.lalamove_status ?? "")) ? (
                        <div className="flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-[#FEE2E2]">
                          <CloseCircleOutlined className="text-[#DC2626]" />
                        </div>
                      ) : (
                        <div className="flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-[#f0ece7]">
                          <CarOutlined className="text-[#8a3b0a]" />
                        </div>
                      )}
                      <div className="flex flex-col gap-2">
                        <div className="text-[#534434] text-xs font-bold uppercase">{t("orderDetail.rider")}</div>
                        {od.rider.name && od.rider.lalamove_status !== "ASSIGNING_DRIVER" ? (
                          <>
                            <div className="text-xl font-bold uppercase leading-none text-[#191c1d]">{od.rider.name}</div>
                            <div className="flex items-center gap-1">
                              <span className="bg-[#C5E7FF] text-[#001E2D] px-2 py-0.5 rounded uppercase font-bold text-[10px] tracking-wide">
                                {od.rider.platform}
                              </span>
                            </div>
                            {od.rider.phone && (
                              <a href={`tel:${od.rider.phone}`} className="text-[#006C49] text-sm flex items-center gap-1">
                                <PhoneOutlined />{od.rider.phone}
                              </a>
                            )}
                            {od.rider.plate_number && (
                              <div className="text-sm font-semibold text-[#191c1d]">{t("orderDetail.riderPlate")}: <span className="font-mono">{od.rider.plate_number}</span></div>
                            )}
                          </>
                        ) : (!deliveryDone && od.status !== "cancelled" && LALAMOVE_FAILED_STATUSES.has(od.rider.lalamove_status ?? "")) ? (
                          <div className="flex flex-col items-start gap-2">
                            <div className="text-base font-semibold text-[#DC2626]">{t("orderDetail.deliveryFailed")}</div>
                            <Button
                              type="primary"
                              loading={lalamoveResubmit.isPending}
                              onClick={handleResubmitLalamove}
                              className="!h-9 !rounded-lg !px-4 !text-xs !font-extrabold !uppercase !tracking-[0.4px]"
                            >
                              {t("orderDetail.resubmitLalamove")}
                            </Button>
                          </div>
                        ) : (
                          <div className="text-base font-semibold text-[#534434]">{od.status === "cancelled" ? t("orderDetail.riderCancelled") : deliveryDone ? t("orderDetail.riderDelivered") : t("orderDetail.findingRider")}</div>
                        )}
                      </div>
                    </div>

                    {od.status !== "cancelled" && (
                      <div className={DETAIL_TIME_CARD_CLASS}>
                        <div className={`${DETAIL_TIME_ICON_CLASS} text-[#ea580c]`}>
                          <Motorbike className="text-[#F59E0B]" />
                        </div>
                        <div className="flex flex-col gap-2">
                          <div className="text-xs font-bold text-[#534434] uppercase">{deliveryDone ? t("orderDetail.deliveredAtLabel") : t("orderDetail.pickupTimeRider")}</div>
                          <div className="text-lg font-extrabold text-[#EA580C] uppercase leading-none">
                            {deliveryDone && (od.rider?.delivered_at ?? od.delivered_at ?? od.completed_at) ? formatOrderTime((od.rider?.delivered_at ?? od.delivered_at ?? od.completed_at)!, i18n.language) : pickupTimeText}
                          </div>
                          <div className="text-xs font-bold text-[#534434] uppercase">{deliveryDone ? t("orderDetail.deliveredNote") : riderPhaseNote}</div>
                        </div>
                      </div>
                    )}
                  </div>
                  {od.status !== "cancelled" && (() => {
                    // ETA only while the trip is still ahead; the distance stays on the rider card after delivery too
                    const showEta = !deliveryDone && !LALAMOVE_FAILED_STATUSES.has(od.rider.lalamove_status ?? "");
                    const eta = showEta ? estimatedArrival(od as never) : null;
                    const km = Number(od.distance_km);
                    // never a silent gap: with no distance on record (the shop had no coordinates when the order was placed) the card says so
                    return (
                      <div className="mt-2.5 text-[13px] font-semibold text-[#534434]">
                        {[eta ? t("orderDetail.etaArrive", { time: formatOrderTime(eta.toISOString(), i18n.language) }) : null, km > 0 ? `${km.toFixed(1)} km` : t("orderDetail.distanceNotRecorded")].filter(Boolean).join(" · ")}
                      </div>
                    );
                  })()}
                  {od.rider.lalamove_order_id && (
                    <div className="mt-2.5 text-[13px] text-[#534434]">
                      {t("orders.drawerLalamoveId")}: <span className="font-mono">{od.rider.lalamove_order_id}</span>
                      {" · "}{t("orders.drawerProgress")}: <b>{od.status === "cancelled" ? t("orderDetail.riderCancelled") : deliveryDone ? t("orderDetail.riderDelivered") : t(lalamoveStatusKey(od.rider.lalamove_status))}</b>
                    </div>
                  )}
                  {od.rider.share_link && od.status !== "cancelled" && (
                    <div className="mt-2.5">
                      <a href={od.rider.share_link} target="_blank" rel="noopener noreferrer" className="text-[13px] font-semibold text-[#006c49]">
                        {t("orderDetail.trackRider")}
                      </a>
                    </div>
                  )}
                </>
              )}

              {/* Customer's rating of this order */}
              {od.rating && (
                <>
                  <Divider className="m-0" />
                  <div className="flex flex-col gap-2" data-testid="order-rating">
                    <div className="text-xs font-bold uppercase text-[#867461]">{t("orderDetail.rating")}</div>
                    <div className="text-lg text-[#F59E0B]" aria-label={`${od.rating.stars}/5`}>{"★".repeat(od.rating.stars)}<span className="text-[#D1D5DB]">{"★".repeat(5 - od.rating.stars)}</span></div>
                    {od.rating.comment && <div className="text-sm text-[#534434]">{od.rating.comment}</div>}
                  </div>
                </>
              )}

              {/* Special note */}
              {displaySpecialNote && (
                <>
                  <Divider className="m-0" />
                  <div className="flex flex-col gap-2">
                    <div className="text-xs font-bold uppercase text-[#867461]">{t("orderDetail.specialNote")}</div>
                    <div className="text-sm uppercase text-[#534434]">
                      {displaySpecialNote}
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* ── Order Items ── */}
            <div className={`${DETAIL_CARD_CLASS} overflow-hidden bg-white`}>
              {/* Header */}
              <div className="flex w-full items-center justify-between mb-6">
                <div className="flex items-center gap-2 text-xl font-bold text-[#191c1d]">
                  <UtensilsCrossed size={18} className="shrink-0 text-[#8b4a2f]" strokeWidth={2.4} aria-hidden="true" />
                  {t("orderDetail.orderItems")}
                </div>
                <span className="text-[13px] text-[#534434]">{t("orderDetail.itemsTotal", { count: totalItems })}</span>
              </div>

              {/* Stall groups */}
              {Array.from(stallGroups.entries()).map(([stallId, items]) => (
                <div className="flex flex-col gap-4 mb-4" key={stallId}>
                  {/* Stall divider — centered pill on a line */}
                  <div className="flex w-full items-center">
                    <div className="h-0.5 flex-1 bg-[#e3e2e1]" />
                    <div className="mx-4 flex items-center gap-2 max-w-[55vw] truncate whitespace-nowrap rounded-full bg-[#eeeeed] px-4 py-1 text-xs font-extrabold uppercase tracking-wide text-[#5a4136]">
                      <UtensilsCrossed size={12} className="text-[#534434]" strokeWidth={2.4} aria-hidden="true" />
                      {stallMap.get(stallId) ?? `${t("orderDetail.stallPrefix")} ${stallId.slice(0, 8)}`}
                    </div>
                    <div className="h-0.5 flex-1 bg-[#e3e2e1]" />
                  </div>

                  {/* Items */}
                  <div className="flex flex-col gap-6">
                    {items.map((item) => {
                      const opts = item.options as Array<{ label: string; extra_charge: string; group?: string }>;
                      const optText = opts.map(o => (o.group ? `${o.group}: ${o.label}` : o.label)).join(", ");
                      const optsExtraTotal = opts.reduce((s, o) => s + Number(o.extra_charge ?? "0"), 0);
                      // the add-on's markup and its SST are two figures (the totals below list them separately too): the line shows both, never the sum as "markup"
                      const optsMarkupOnly = Number((item as { options_markup_amount?: string }).options_markup_amount ?? 0);
                      const optsSst = Number((item as { options_sst_amount?: string }).options_sst_amount ?? 0);
                      const optsMarkupTotal = optsMarkupOnly + optsSst;
                      const lineTotal = (Number(item.price) + optsExtraTotal + optsMarkupTotal) * item.qty;
                      const rejected = isRejectedLine(item);
                      return (
                        <div key={item.id} className={`flex items-center gap-3.5${rejected ? " opacity-50 line-through" : ""}`}>
                          {/* Thumbnail */}
                          {(item as { image_url?: string }).image_url ? (
                            <img
                              src={(item as { image_url?: string }).image_url}
                              alt={item.name}
                              className="size-12 max-[480px]:size-9 shrink-0 rounded-lg object-cover"
                            />
                          ) : (
                            <div className="flex size-12 max-[480px]:size-9 shrink-0 items-center justify-center rounded-lg bg-[#f3f4f5] text-[22px] font-bold text-[#534434]">
                              {item.name.slice(0, 1).toUpperCase()}
                            </div>
                          )}

                          {/* Name + options */}
                          <div className="min-w-0 flex-1 flex flex-col gap-0.5">
                            <div className="text-base font-semibold leading-[1.3] text-[#191c1d]">{item.name}</div>
                            {optText && <div className="text-sm text-[#534434]">{optText}</div>}
                            {item.note && <div className="text-sm italic text-[#534434]">"{item.note}"</div>}
                            {Number((item as { markup_amount?: string }).markup_amount ?? 0) > 0 && (
                              <div className="text-xs text-[#8b4a2f]">
                                {t("orderDetail.basePriceMarkup", {
                                  base: Number((item as { base_price?: string }).base_price ?? 0).toFixed(2),
                                  markup: Number((item as { markup_amount?: string }).markup_amount ?? 0).toFixed(2),
                                })}
                                {Number((item as { sst_amount?: string }).sst_amount ?? 0) > 0 && ` + SST RM ${Number((item as { sst_amount?: string }).sst_amount).toFixed(2)}`}
                              </div>
                            )}
                            {optsExtraTotal > 0 && (
                              <div className="text-xs text-[#8b4a2f]">
                                {t("orderDetail.addonCostMarkup", {
                                  cost: optsExtraTotal.toFixed(2),
                                  markup: optsMarkupOnly.toFixed(2),
                                })}
                                {optsSst > 0 && ` + SST RM ${optsSst.toFixed(2)}`}
                              </div>
                            )}
                          </div>

                          {/* Qty badge */}
                          <div className="flex size-12 max-[480px]:size-9 shrink-0 items-center justify-center rounded-xl bg-[#f6f6f6] text-base font-bold text-[#ea580c]">
                            {item.qty}x
                          </div>

                          {/* Price */}
                          <div className="min-w-[72px] max-[480px]:min-w-[56px] max-[480px]:text-xs shrink-0 text-right text-sm font-semibold text-[#191c1d]">
                            RM {lineTotal.toFixed(2)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}

              {/* Price breakdown */}
              <div className="w-full border-t border-dashed border-[#e5e7eb] pt-4 mt-2">
                <div className="flex flex-col gap-4">
                  <div className="flex justify-between text-xs text-[#867461]">
                    <span>{t("orderDetail.basePriceTotal")}</span>
                    <span>RM {orderTotals.basePriceTotal.toFixed(2)}</span>
                  </div>
                  {orderTotals.markupTotal > 0 && (
                    <div className="flex justify-between text-xs text-[#867461]">
                      <span>{t("orderDetail.markupTotal")}</span>
                      <span>RM {orderTotals.markupTotal.toFixed(2)}</span>
                    </div>
                  )}
                  {orderTotals.addonCostTotal > 0 && (
                    <div className="flex justify-between text-xs text-[#867461]">
                      <span>{t("orderDetail.addonCostTotal")}</span>
                      <span>RM {orderTotals.addonCostTotal.toFixed(2)}</span>
                    </div>
                  )}
                  {orderTotals.addonMarkupTotal > 0 && (
                    <div className="flex justify-between text-xs text-[#867461]">
                      <span>{t("orderDetail.addonMarkupTotal")}</span>
                      <span>RM {orderTotals.addonMarkupTotal.toFixed(2)}</span>
                    </div>
                  )}
                  {sstIncluded && orderTotals.sstTotal > 0 && (
                    <div className="flex justify-between text-xs text-[#867461]">
                      <span>{t("orderDetail.sstIncludedRow")}</span>
                      <span>RM {orderTotals.sstTotal.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm text-[#534434]">
                    <span>{t("orderDetail.subtotal")}</span>
                    <span>RM {Number(od.subtotal).toFixed(2)}</span>
                  </div>
                  {Number(od.voucher_discount) > 0 && (
                    <div className="flex justify-between text-sm text-[#006c49]">
                      <span className="flex items-center gap-1.5">
                        <TagOutlined className="text-[13px]" />
                        {t("orders.discountWithCode", { code: od.voucher_code ?? t("orders.voucherFallback") })}
                      </span>
                      <span className="text-[#ea580c]">−RM {Number(od.voucher_discount).toFixed(2)}</span>
                    </div>
                  )}
                  {Number(od.delivery_fee) > 0 && (
                    <div className="flex justify-between text-sm text-[#534434]">
                      <span>{t("orderDetail.deliveryFee")}</span>
                      <span>RM {Number(od.delivery_fee).toFixed(2)}</span>
                    </div>
                  )}
                  {Number(od.sst_amount) > 0 && !sstIncluded && (
                    <div className="flex justify-between text-sm text-[#534434]">
                      <span>{t("orderDetail.sstPercent", { rate: (Number((od as { sst_rate?: string }).sst_rate ?? 0.06) * 100).toFixed(2) })}</span>
                      <span>RM {Number(od.sst_amount).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex items-baseline justify-between border-t border-[#e5e7eb] pt-3">
                    <span className="text-[15px] font-extrabold text-[#191c1d]">{t("orderDetail.total")}</span>
                    <span className="text-[22px] font-extrabold text-[#ea580c]">RM {Number(od.total).toFixed(2)}</span>
                  </div>
                  {customerPaidAmount > 0 && (
                    <div className="flex justify-between text-sm text-[#006c49]">
                      <span>{t("orderDetail.customerPaidAmount")}</span>
                      <span className="font-bold">RM {customerPaidAmount.toFixed(2)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* ── Refund History ── */}
            <div className={`${DETAIL_CARD_CLASS} bg-white !px-7 !py-6`}>
              {/* Header row */}
              <div className={`flex items-center justify-between gap-4 ${refunds.length > 0 ? "mb-5" : ""}`}>
                <div>
                  <div className="text-[17px] font-bold leading-[1.3] text-[#191c1d]">{t("orderDetail.refundHistory")}</div>
                  {refunds.length === 0 && (
                    <div className="mt-1 text-[13px] text-[#867461]">{t("orderDetail.noRefunds")}</div>
                  )}
                </div>
                <Button
                  onClick={openRefundForm}
                  className="!shrink-0 !rounded-lg !text-sm !font-semibold"
                >
                  {t("orderDetail.createRefund")}
                </Button>
              </div>

              {/* Refund rows */}
              {refunds.length > 0 && (
                <div className="flex flex-col gap-2.5">
                  {refunds.map(ref => (
                    <div key={ref.id} className="flex items-center justify-between rounded-[10px] border border-[#f3f4f6] bg-[#fafafa] px-4 py-3.5">
                      <div className="min-w-0">
                        <div className="text-sm font-semibold leading-[1.3] text-[#191c1d]">{cancelReasonLabel(ref.reason, i18n.language)}</div>
                        <div className="mt-1 text-xs text-[#867461]">
                          {formatDateTimeDetail(ref.created_at, i18n.language)}
                          {" · "}
                          <span className="font-semibold">{payMethodLabel(ref.method, i18n.language) ?? ref.method}</span>
                        </div>
                      </div>
                      <div className="ml-4 shrink-0 text-right">
                        <div className="text-[15px] font-bold text-[#dc2626]">
                          -RM {Number(ref.amount).toFixed(2)}
                        </div>
                        <StatusBadge
                          status={ref.status}
                          kind="refund"
                          label={t(`orderDetail.refundRowStatus.${ref.status}`, { defaultValue: ref.status })}
                          className="mt-1"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ══ RIGHT SIDEBAR ══ */}
          <div className={DETAIL_SIDE_PANEL_CLASS}>

            {/* ── Transaction Details ── */}
            <div className="overflow-hidden rounded-3xl border border-[#e5e7eb] bg-white shadow-[0_12px_16px_rgba(25,28,29,0.04)]">
              {/* Teal top accent */}
              <div className="h-1 bg-[#006c49]" />
              <div className="px-5 pb-5 pt-4">
                <div className="mb-4 text-[11px] font-bold uppercase tracking-[1px] text-[#191c1d]">
                  {t("orderDetail.transactionDetails")}
                </div>
                {txn ? (
                  <>
                    {/* Method */}
                    <div className="flex items-center justify-between border-b border-[#f3f4f6] pb-3">
                      <span className="text-[13px] text-[#867461]">{t("orderDetail.method")}</span>
                      <span className="flex items-center gap-1.5 text-[13px] font-semibold text-[#191c1d]">
                        <WalletOutlined className="text-sm text-[#006c49]" />
                        {paymentMethodLabel(t, txn.method)}
                      </span>
                    </div>
                    {/* Status */}
                    <div className="flex items-center justify-between border-b border-[#f3f4f6] py-3">
                      <span className="text-[13px] text-[#867461]">{t("orderDetail.status")}</span>
                      <StatusBadge
                        status={txn.status}
                        kind="transaction"
                        label={txn.status === "success" ? t("orderDetail.paidUpper") : txn.status === "pending" ? t("orderDetail.txnPending") : undefined}
                      />
                    </div>
                    {/* Transaction ID */}
                    <div className="flex items-center justify-between border-b border-[#f3f4f6] py-3">
                      <span className="text-[13px] text-[#867461]">{t("orderDetail.transactionId")}</span>
                      <span className="font-mono text-[13px] font-medium text-[#191c1d]">
                        {formatTransactionId(txn.id)}
                      </span>
                    </div>
                    {/* View Transaction button */}
                    <Button
                      block
                      className="!mt-4 !h-10 !rounded-lg !border !border-[#e5e7eb] !text-sm !font-semibold"
                      onClick={() => navigate(`/transactions/${txn.id}`, { state: { activeNav: "/transactions" } })}
                    >
                      {t("orderDetail.viewTransaction")}
                    </Button>
                  </>
                ) : (
                  <div className="py-4 text-center text-[13px] text-[#867461]">
                    {t("orderDetail.noTxnRecord")}
                  </div>
                )}
              </div>
            </div>

            {/* ── Order Journey ── */}
            <div className="rounded-3xl border border-[#e5e7eb] bg-white p-6 shadow-[0_12px_16px_rgba(25,28,29,0.04)]">
              <div className="mb-6 text-[11px] font-bold uppercase tracking-[1px] text-[#867461]">
                {t("orderDetail.orderJourney")}
              </div>
              <div className="flex flex-col gap-0">
                {journey.map((step, idx) => (
                  <JourneyStep key={idx} step={step} isLast={idx === journey.length - 1} />
                ))}
              </div>
            </div>


          </div>
        </div>
      </div>

      {/* ── Sticky footer ── */}
      <div className={DETAIL_FOOTER_BAR_CLASS}>
        <div className={DETAIL_FOOTER_INNER_CLASS}>
          {canCancelOrder && (
            <>
              <Button
                type="text"
                icon={<CloseCircleOutlined className="text-base" />}
                className={`${DETAIL_FOOTER_BUTTON_CLASS} !border-none !bg-transparent !px-4 !text-[#dc2626]`}
                onClick={() => setCancelOpen(true)}
                disabled={cancelOrder.isPending}
              >
                {t("orderDetail.cancelOrder")}
              </Button>
              <div className="h-7 w-px bg-[#e5e7eb]" />
            </>
          )}

          {/* Print — grey bordered */}
          <Button
            icon={<PrinterOutlined className="text-[15px]" />}
            className={`${DETAIL_FOOTER_BUTTON_CLASS} !border !border-transparent !bg-[#f3f4f5] !text-[#191c1d] shadow-none`}
            onClick={handleDownloadReceipt}
            disabled={!txn?.id || isReceiptFetching}
          >
            {t("orderDetail.printReceipt")}
          </Button>

          {/* Status action */}
          <FooterAction />
        </div>
      </div>

      {/* ── Cancel modal ── */}
      <OrderCancelModal
        open={cancelOpen && !!od.id}
        orderCode={od.order_code}
        onCancel={() => { setCancelOpen(false); }}
        onConfirm={handleCancel}
        loading={cancelOrder.isPending}
      />

      <Modal
        open={refundNoPaymentOpen}
        onCancel={() => setRefundNoPaymentOpen(false)}
        footer={
          <Button
            type="primary"
            onClick={() => setRefundNoPaymentOpen(false)}
            className="h-11 min-w-[120px] rounded-xl !border-transparent !bg-[#ea580c] px-5 text-sm font-extrabold !text-white shadow-[0_10px_24px_rgba(234,88,12,0.18)] hover:!border-transparent hover:!bg-[#c2410c] hover:!text-white"
          >
            {t("common.confirm")}
          </Button>
        }
        title={
          <div className="rounded-2xl border border-[#eee8e2] bg-white p-5">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex size-10 shrink-0 items-center justify-center rounded-xl bg-[#fff7ed] text-[#ea580c]">
                  <WalletOutlined className="text-[19px]" />
                </div>
                <div>
                  <div className="text-lg font-black leading-6 text-[#191c1d]">{t("orderDetail.refundNoPaymentTitle")}</div>
                  <div className="mt-1 max-w-[420px] text-sm font-medium leading-5 text-[#867461]">
                    {t("orderDetail.refundNoPaymentDesc")}
                  </div>
                </div>
              </div>
              <span className="shrink-0 rounded-full bg-[#f3f4f5] px-3 py-1 text-xs font-black uppercase tracking-[0.5px] text-[#006c49]">
                #{od.order_code}
              </span>
            </div>
          </div>
        }
      >
        <div className="mt-4 rounded-2xl border border-[#eee8e2] bg-[#fffaf5] p-4 text-sm font-semibold leading-6 text-[#534434]">
          {t("orderDetail.refundNoPaidTxn")}
        </div>
      </Modal>

    </AdminShell>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function SidebarRow({
  label, value, mono = false, bold = false,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
  bold?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="shrink-0 text-xs text-[#867461]">{label}</span>
      <span className={`text-right text-[13px] text-[#191c1d] ${bold ? "font-bold" : "font-medium"} ${mono ? "font-mono" : ""}`}>
        {value}
      </span>
    </div>
  );
}

function JourneyStep({ step, isLast }: { step: JStep; isLast: boolean }) {
  const { t, i18n } = useTranslation();
  const isDone   = step.state === "done";
  const isActive = step.state === "active";
  const isPending = step.state === "pending";
  const isCancelled = step.tone === "cancelled";

  const dotClass = isCancelled
    ? "bg-[#ef4444] shadow-[0_0_0_4px_rgba(239,68,68,0.15)]"
    : isDone
    ? "bg-[#10b981] shadow-[0_0_0_4px_rgba(16,185,129,0.15)]"
    : isActive
    ? "bg-[#f59e0b] shadow-[0_0_0_4px_rgba(245,158,11,0.15)]"
    : "bg-[#cbd5e1]";

  const timeStr = step.time
    ? step.showDateTime
      ? formatDateTimeDetail(step.time, i18n.language)
      : formatOrderTime(step.time, i18n.language)
    : null;
  const subtext = isDone
    ? (timeStr ?? "")
    : isActive
    ? (timeStr ? t("orderDetail.journeyTimeCurrent", { time: timeStr }) : t("orderDetail.journeyCurrent"))
    : (step.subtext ?? t("orderDetail.journeyPending"));

  return (
    <div className="group flex gap-4">
      <div className="flex flex-col items-center">
        <div className={`z-10 mt-1 size-2.5 rounded-full ${dotClass}`} />
        {!isLast && <div className="my-2 w-[2px] flex-1 rounded-full bg-[#f1f5f9] transition-colors group-hover:bg-[#e2e8f0]" />}
      </div>
      <div className={`flex-1 ${!isLast ? "pb-6" : ""}`}>
        {timeStr && (
          <div className="mb-1 text-[10px] font-black uppercase tracking-[0.8px] text-[#94a3b8]">
            {timeStr}
          </div>
        )}
        <div className="text-sm font-extrabold text-[#0f172a]">
          {step.label}
        </div>
        {subtext && subtext !== timeStr && (
          <div className="mt-2 rounded-xl bg-[#f8fafc] p-3 text-xs font-semibold leading-relaxed text-[#64748b]">
            {subtext}
          </div>
        )}
      </div>
    </div>
  );
}
